import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Svg } from '@hopskipdrive/react-components';
import { useQueryClient } from '@tanstack/react-query';
import { includes } from 'lodash';

import { Button } from '../../../../../shared/components/Button';
import { colorTokens } from '../../../../../shared/components/variables';
import { useToast } from '../../../../../shared/contexts/ToastContext';
import { RiderRemovalsModal } from '../../../../components/RiderRemovalsModal';
import { useRemoveRidersSubmit } from '../../../../components/RiderRemovalsModal/useRemoveRidersSubmit';
import { RIDE_STATES } from '../../../../constants/rideStates';
import { RIDE_DETAILS_QUERY_KEY } from '../../../../hooks/useRideDetailsQuery';
import { RideDetails } from '../../../../types/rides';

interface RemoveRiderProps {
  riderSlug: string;
}

export const RemoveRider: FC<RemoveRiderProps> = ({ riderSlug }) => {
  const { rideSlug } = useParams();
  const queryClient = useQueryClient();
  const rideDetailsResponse = queryClient.getQueryData<{ data: RideDetails }>([RIDE_DETAILS_QUERY_KEY, rideSlug]);
  const canRemoveRider = !includes(
    [...RIDE_STATES.completed, ...RIDE_STATES.inactive, ...RIDE_STATES.canceled],
    rideDetailsResponse?.data?.state
  );
  const riderToRemove = rideDetailsResponse?.data?.riders?.find((rider) => rider.slug === riderSlug);
  const [riderRemovalModalOpen, setRiderRemovalModalOpen] = useState<boolean>();
  const { addToast } = useToast();
  const { mutate: removeRidersMutate, isPending: isPendingRemoveRiders } = useRemoveRidersSubmit({
    rideSlug: rideSlug || '',
    successCallback: () => {
      addToast({
        severity: 'success',
        message: 'Rider Removed',
      });
      // Optimistically update the query data to remove the rider from the list
      // and add them to the removedRiders list
      queryClient.setQueryData<{ data: RideDetails }>([RIDE_DETAILS_QUERY_KEY, rideSlug], (prevRideDetailsData) => {
        if (!prevRideDetailsData || !prevRideDetailsData.data.id) {
          return prevRideDetailsData; // Return unchanged if data or id is missing
        }

        const remainingRiders = prevRideDetailsData.data.riders?.filter((rider) => rider.slug !== riderSlug);

        if (remainingRiders.length === 0) {
          queryClient.invalidateQueries({ queryKey: [RIDE_DETAILS_QUERY_KEY, rideSlug] });
        }

        return {
          ...prevRideDetailsData,
          data: {
            ...prevRideDetailsData.data,
            riders: remainingRiders,
            removedRiders: [
              ...(prevRideDetailsData.data.removedRiders || []),
              ...(prevRideDetailsData.data.riders?.filter((rider) => rider.slug === riderSlug) || []),
            ],
          },
        };
      });
      setRiderRemovalModalOpen(false);
    },
    errorCallback: (
      error: Error & {
        errors: string[];
      }
    ) => {
      error?.errors
        ? error?.errors?.forEach((errorMessage) => {
            addToast({
              severity: 'error',
              message: errorMessage,
            });
          })
        : addToast({
            severity: 'error',
            message: error.message,
          });
    },
  });

  return (
    <>
      {canRemoveRider && (
        <Button
          color="warning"
          startIcon={<Svg icon="cross" />}
          style={{
            border: `1px solid ${colorTokens.error.outlineBorder}`,
            color: `${colorTokens.error.dark}`,
          }}
          onClick={() => setRiderRemovalModalOpen(true)}
        >
          Remove Rider
        </Button>
      )}
      {riderRemovalModalOpen && riderToRemove && (
        <RiderRemovalsModal
          onClose={() => {
            setRiderRemovalModalOpen(undefined);
          }}
          reasons={rideDetailsResponse?.data?.rideCancellationReasons || []}
          onRemove={removeRidersMutate}
          ridersToRemove={[riderToRemove]}
          isPending={isPendingRemoveRiders}
        />
      )}
    </>
  );
};
